// File: js/db/transport.js
//
// The real Supabase transport (spec §6/§7). Nothing here holds application logic:
// it marshals arguments, unwraps responses, and paginates. The sync engine takes
// this object by injection, which is what lets the whole engine be tested against
// a fake server with no network.
//
// The Supabase client is a PARAMETER, never an import. `supabaseClient.js` reads
// `process.env` at module scope and writes into `document` when the keys are
// missing, so importing it would make this module unloadable in Node and in any
// test. The application passes the client in; step 4 wires it once.
//
// ── PAGINATE OR REFUSE ──────────────────────────────────────────────────────
// EVERY read below loops `.range()` on a stable key until a short page comes back.
// supabase-js returns a truncated 1000-row page WITHOUT an error, and every sum
// computed over that array is simply wrong and lower than reality, with no error
// and no indication — audit C6, where `reporting.js` and `receiptManagement.js`
// select with no pagination at all and an annual report is off by an order of
// magnitude. Silent truncation is wrong money and is forbidden (spec §8).
//
// Every read result is passed through `money/dedupe.js`: offset pagination over a
// live table and the 10-minute pull overlap both legitimately return the same row
// twice, and the ledger id is the identity of a real-world sale (invariant 2), so
// a second copy is the SAME event, never a second one.

import { dedupeById } from '../money/dedupe.js';

/** PostgREST's silent-truncation boundary. */
export const PAGE_SIZE = 1000;

/**
 * The full ledger column set. Named explicitly rather than `select('*')` so a
 * column added server-side never silently changes what the folds see.
 */
const LEDGER_COLUMNS = [
    'id', 'kind', 'channel', 'amount', 'reverses_id', 'reason',
    'items', 'patient_name', 'receipt_no', 'book_no',
    'collected_by', 'recorded_by', 'device_id',
    'device_time', 'server_received_at', 'business_date', 'clock_skew_flag',
    'legacy_receipt_number'
].join(', ');

/**
 * Fetch EVERY row of a query, one `.range()` page at a time.
 *
 * This is the single place the "a full final page must fetch again" rule lives:
 * the loop stops ONLY on a short page, so `rows.length === pageSize` always
 * triggers a follow-up fetch by construction. A 1000-row result costs two round
 * trips — the price of never truncating silently.
 *
 * @param {(from:number, to:number) => PromiseLike<{data:object[]|null, error:object|null}>} fetchPage
 *   Builds and executes ONE page for the inclusive range [from, to]. The builder
 *   must re-apply its ordering on every call: ordering is per-request in PostgREST
 *   and is never remembered between pages.
 * @param {number} [pageSize] Injectable for tests.
 * @returns {Promise<object[]>} All rows, page order preserved.
 * @throws On any page error — a partial fetch refuses, it never renders.
 */
export async function fetchAllPages(fetchPage, pageSize = PAGE_SIZE) {
    const rows = [];
    for (let from = 0; ; from += pageSize) {
        const { data, error } = await fetchPage(from, from + pageSize - 1);
        if (error) throw new Error(error.message || String(error));
        const page = data ?? [];
        rows.push(...page);
        if (page.length < pageSize) return rows;
    }
}

/**
 * Unwrap an RPC response, turning a PostgREST error into a thrown transport
 * failure. The sync engine reads a THROW as "keep the outbox and back off"; it
 * must never see a null it could mistake for an empty ack list.
 * @param {{data:*, error:*}} res
 * @returns {*}
 */
function unwrapRpc(res) {
    if (res && res.error) {
        const err = new Error(res.error.message || String(res.error));
        err.code = res.error.code;
        err.details = res.error.details;
        throw err;
    }
    return res ? res.data : null;
}

/**
 * Build the application transport over a Supabase client.
 *
 * @param {{rpc: Function, from: Function}} client A supabase-js client.
 * @param {{pageSize?: number}} [options]
 * @returns {object}
 */
export function createSupabaseTransport(client, options = {}) {
    const pageSize = options.pageSize ?? PAGE_SIZE;

    return {
        // ---- push ----------------------------------------------------------

        /**
         * Drain a batch through the single validation path (spec §6).
         * `sync_push` does NOT pin `kind`; the wire kind is honoured and
         * `apply_money_event` forbids what the batch may not smuggle.
         * @param {object[]} events
         * @returns {Promise<{id:string, status:string, reason?:string}[]>}
         */
        async syncPush(events) {
            const data = unwrapRpc(await client.rpc('sync_push', { events }));
            return Array.isArray(data) ? data : [];
        },

        // ---- online-only single writes (spec §6) ---------------------------
        //
        // void · discount · restock · stock_adjustment are admin-only and
        // online-only: they never enter the outbox, so they have no offline path
        // and no idempotency retry loop. Each returns the frozen §6 verdict
        // `{id, status, reason?}` — the caller checks `status`, it does not assume.

        /** @param {object} event @returns {Promise<{id:string, status:string, reason?:string}>} */
        async recordSale(event) {
            return unwrapRpc(await client.rpc('record_sale', { event }));
        },
        /** @param {object} event */
        async recordVoid(event) {
            return unwrapRpc(await client.rpc('record_void', { event }));
        },
        /** @param {object} event */
        async recordDiscount(event) {
            return unwrapRpc(await client.rpc('record_discount', { event }));
        },
        /** @param {object} event */
        async recordRestock(event) {
            return unwrapRpc(await client.rpc('record_restock', { event }));
        },
        /** @param {object} event */
        async recordStockAdjustment(event) {
            return unwrapRpc(await client.rpc('record_stock_adjustment', { event }));
        },

        // ---- device + receipt supply ---------------------------------------

        /**
         * @param {{deviceId:string, reason:'initial'|'low_watermark'|'meta_loss', size?:number}} q
         * @returns {Promise<{block_id:string, block_start:number, block_end:number, reason:string}>}
         */
        async allocateReceiptBlock(q) {
            const params = { p_device_id: q.deviceId, p_reason: q.reason };
            if (q.size != null) params.p_size = q.size;
            return unwrapRpc(await client.rpc('allocate_receipt_block', params));
        },

        /** @param {string} name @returns {Promise<{id:string, name:string}>} */
        async registerDevice(name) {
            return unwrapRpc(await client.rpc('register_device', { p_name: name }));
        },

        // ---- conflict resolution + day close --------------------------------

        /**
         * @param {{conflictId:string, action:'reinject'|'reject', note?:string|null}} q
         */
        async resolveConflict(q) {
            return unwrapRpc(await client.rpc('resolve_conflict', {
                p_conflict_id: q.conflictId,
                p_action: q.action,
                p_note: q.note ?? null
            }));
        },

        /**
         * @param {{deviceId:string, businessDate:string, expectedCash:number,
         *   countedCash:number, unsyncedCount?:number, note?:string|null}} q
         */
        async closeDay(q) {
            return unwrapRpc(await client.rpc('close_day', {
                p_device_id: q.deviceId,
                p_business_date: q.businessDate,
                p_expected_cash: q.expectedCash,
                p_counted_cash: q.countedCash,
                p_unsynced_count: q.unsyncedCount ?? 0,
                p_note: q.note ?? null
            }));
        },

        // ---- paginated reads ------------------------------------------------

        /**
         * The sync engine's incremental pull.
         *
         * Ordered by `(server_received_at, id)` — a stable composite key, because
         * `server_received_at` alone is not unique and offset pagination over a
         * non-unique order can drop or repeat a row between pages.
         *
         * NOT filtered by kind, channel, device or date. Spec §8: "Never filter
         * server-side on a dimension the cancelling event does not carry." A `void`
         * carries no `channel`, so a server-side channel filter would drop voids
         * from the set and a voided sale would silently re-count. Membership and
         * grouping are fold-side only.
         *
         * @param {{since:string|null}} q `since` already carries the overlap window.
         * @returns {Promise<object[]>}
         */
        async pullEvents(q) {
            const since = q && q.since != null ? q.since : null;
            const rows = await fetchAllPages((from, to) => {
                let query = client.from('money_ledger').select(LEDGER_COLUMNS);
                if (since != null) query = query.gte('server_received_at', since);
                return query
                    .order('server_received_at', { ascending: true })
                    .order('id', { ascending: true })
                    .range(from, to);
            }, pageSize);
            return dedupeById(rows);
        },

        /**
         * This device's parked events. A conflict row's `device_id` is a
         * denormalized copy and deliberately not an FK — an unknown device can be
         * the very reason an event parked — so the filter is on that copy.
         *
         * `deviceId == null` (unprovisioned) reads nothing rather than everything:
         * a device with no identity has no queue of its own to show.
         *
         * @param {{deviceId:string|null}} q
         * @returns {Promise<object[]>}
         */
        async pullConflicts(q) {
            const deviceId = q && q.deviceId != null ? q.deviceId : null;
            if (deviceId == null) return [];
            return fetchAllPages((from, to) => client
                .from('conflicts')
                .select('*')
                .eq('device_id', deviceId)
                .order('raised_at', { ascending: true })
                .order('id', { ascending: true })
                .range(from, to), pageSize);
        },

        /**
         * Every ledger event in a business-date range, complete (audit C6).
         *
         * The bounds are inclusive CALENDAR dates, not instants: `business_date` is
         * a `date` column the server derives at fixed UTC+01:00 (invariant 9), so
         * comparing it against an instant is the very timezone confusion that lost
         * an hour from every daily report (audit H1). Callers holding instants
         * should use `businessDateOf()` from `money/` to get the date strings.
         *
         * Reports must still fold, never trust this set's membership: a `void`
         * carries the business_date it was AUTHORED on, so a void of last month's
         * sale falls outside this month's range. A report that needs the corrected
         * position of a sale must widen the range or fold over full history.
         *
         * @param {{fromDate:string, toDate:string}} q `YYYY-MM-DD`, both inclusive.
         * @returns {Promise<object[]>}
         */
        async fetchLedgerBetween(q) {
            const rows = await fetchAllPages((from, to) => client
                .from('money_ledger')
                .select(LEDGER_COLUMNS)
                .gte('business_date', q.fromDate)
                .lte('business_date', q.toDate)
                .order('business_date', { ascending: true })
                .order('id', { ascending: true })
                .range(from, to), pageSize);
            return dedupeById(rows);
        },

        /**
         * Every ledger event, ever. The input for a stock fold, which has no date
         * window: a drug's position is the fold of its entire history.
         * @returns {Promise<object[]>}
         */
        async fetchAllLedger() {
            const rows = await fetchAllPages((from, to) => client
                .from('money_ledger')
                .select(LEDGER_COLUMNS)
                .order('id', { ascending: true })
                .range(from, to), pageSize);
            return dedupeById(rows);
        },

        /**
         * The admin's conflict queue, every device. `status` filters to `'open'` by
         * default — the queue a human is asked to act on.
         * @param {{status?:string|null}} [q]
         * @returns {Promise<object[]>}
         */
        async fetchConflicts(q = {}) {
            const status = q.status === undefined ? 'open' : q.status;
            return fetchAllPages((from, to) => {
                let query = client.from('conflicts').select('*');
                if (status != null) query = query.eq('status', status);
                return query
                    .order('raised_at', { ascending: true })
                    .order('id', { ascending: true })
                    .range(from, to);
            }, pageSize);
        },

        /**
         * A named view, paginated on a caller-supplied stable key.
         * @param {{view:string, orderBy:string[]}} q
         * @returns {Promise<object[]>}
         */
        async fetchView(q) {
            return fetchAllPages((from, to) => {
                let query = client.from(q.view).select('*');
                for (const col of q.orderBy) query = query.order(col, { ascending: true });
                return query.range(from, to);
            }, pageSize);
        }
    };
}
